import React, { useMemo } from "react";
import {
  AbsoluteFill,
  Audio,
  Easing,
  Img,
  Sequence,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { getAudioDurationInSeconds } from "@remotion/media-utils";
import { VIDEO_FONT_FAMILY } from "../RegisterVideoFonts";
import { clampDurationInFrames, clampFps } from "../lib/renderLimits";
import { fadeUpEaseOut, springs } from "../lib/animations";
import { parseClipInputProps } from "./scenarioExplainerSchema";
import {
  buildCaptionChunks,
  buildCaptionWords,
  buildTimeline,
  formatAmountOrUnlimited,
  formatEmphasis,
  formatNetDebitCredit,
  formatPrice,
  resolveVoiceoverSeconds,
} from "./scenarioExplainerTimeline";
import { getSocialLayout } from "./scenarioExplainerLayout";
import { PayoffChart, PayoffLegend } from "./PayoffChart";
import { SocialCaptions } from "./SocialCaptions";

export const FIXED_DISCLAIMER_LINE = "Educational tool. Not investment advice.";

export function modeledDataLine(dataAsOf) {
  return `Data as of ${dataAsOf}. Results are modeled, not actual trades.`;
}

const clampOpts = { extrapolateLeft: "clamp", extrapolateRight: "clamp" };

// ── Metadata ────────────────────────────────────────────────

/**
 * Validates props (throws → render fails), resolves voiceover length and
 * computes duration from beats/audio, clamped by renderLimits.
 */
export async function calculateScenarioExplainerMetadata({ props }) {
  const parsed = parseClipInputProps(props);
  const fps = clampFps(parsed.fps ?? 30);
  let probed = 0;
  const vo = parsed.voiceover;
  if (vo && !vo.durationSeconds && !vo.words.length) {
    // No timing metadata: measure the file. Unreachable audio fails the render.
    probed = await getAudioDurationInSeconds(vo.audioUrl);
  }
  const voiceoverSeconds = resolveVoiceoverSeconds(vo, probed);
  const normalized = {
    ...parsed,
    fps,
    voiceover: vo ? { ...vo, durationSeconds: voiceoverSeconds || null } : null,
  };
  const timeline = buildTimeline(normalized, fps, voiceoverSeconds);
  return {
    durationInFrames: clampDurationInFrames(timeline.totalFrames, fps),
    fps,
    props: { ...props, clip: normalized },
  };
}

export async function calculateScenarioExplainerThumbnailMetadata({ props }) {
  const parsed = parseClipInputProps(props);
  return { durationInFrames: 1, props: { ...props, clip: parsed } };
}

// ── Shared hooks / pieces ───────────────────────────────────

function useClipModel(rawProps) {
  const { fps, width, height } = useVideoConfig();
  return useMemo(() => {
    const props = parseClipInputProps(rawProps);
    const timeline = buildTimeline(props, fps, resolveVoiceoverSeconds(props.voiceover));
    const words = buildCaptionWords(props, timeline);
    const chunks = buildCaptionChunks(words);
    const layout = getSocialLayout(width, height);
    return { props, timeline, words, chunks, layout };
  }, [rawProps, fps, width, height]);
}

function footerText(disclaimer) {
  return `${FIXED_DISCLAIMER_LINE} ${disclaimer}`;
}

/** Height estimate for the wrapped footer so the stage can be sized up-front. */
function estimateFooterHeight(text, fontSize, contentWidth) {
  const charsPerLine = Math.max(10, Math.floor(contentWidth / (fontSize * 0.52)));
  const lines = Math.min(4, Math.ceil(text.length / charsPerLine));
  return Math.ceil(lines * fontSize * 1.35);
}

function computeFrame(layout, props, width, height) {
  const { s, L, safeTop, safeBottom, sidePad } = layout;
  const contentWidth = width - sidePad * 2;
  const gap = s(L.gap);
  const headerHeight = s(L.headerHeight);
  const captionHeight = s(L.captionBoxHeight);
  const footerFont = s(L.footerFont);
  const footerHeight = estimateFooterHeight(footerText(props.disclaimer), footerFont, contentWidth);
  const stageHeight =
    height - safeTop - safeBottom - headerHeight - captionHeight - footerHeight - gap * 3;
  return { contentWidth, gap, headerHeight, captionHeight, footerFont, footerHeight, stageHeight };
}

function BrandMark({ brand, size, fontSize }) {
  const { colors } = brand;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: size * 0.3 }}>
      {brand.logoUrl ? (
        <Img src={brand.logoUrl} style={{ height: size, width: "auto", objectFit: "contain" }} />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: size * 0.24,
            background: colors.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF",
            fontWeight: 800,
            fontSize: size * 0.56,
            lineHeight: 1,
          }}
        >
          {brand.name.charAt(0).toUpperCase()}
        </div>
      )}
      <span style={{ fontSize, fontWeight: 800, color: colors.text, letterSpacing: "-0.3px" }}>{brand.name}</span>
    </div>
  );
}

function Chip({ children, colors, fontSize, style = {} }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: `${fontSize * 0.3}px ${fontSize * 0.7}px`,
        borderRadius: fontSize * 2,
        background: colors.surface,
        color: colors.accent,
        fontSize,
        fontWeight: 800,
        letterSpacing: "0.5px",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

function Header({ props, layout, height }) {
  const { s, L } = layout;
  const { brand } = props;
  return (
    <div style={{ height, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <BrandMark brand={brand} size={s(L.logoSize)} fontSize={s(L.brandFont)} />
      <Chip colors={brand.colors} fontSize={s(L.chipFont)}>
        {`${props.ticker} · ${props.strategyLabel}`}
      </Chip>
    </div>
  );
}

function Footer({ props, fontSize, height, opacity }) {
  return (
    <div
      style={{
        height,
        flexShrink: 0,
        overflow: "hidden",
        opacity,
        fontSize,
        lineHeight: 1.35,
        fontWeight: 500,
        color: props.brand.colors.muted,
      }}
    >
      {footerText(props.disclaimer)}
    </div>
  );
}

/** Opacity that fades a card out over its last frames. */
function useExitOpacity(durationInFrames, fadeFrames = 6) {
  const frame = useCurrentFrame();
  return interpolate(frame, [durationInFrames - fadeFrames, durationInFrames], [1, 0], clampOpts);
}

// ── Segments ────────────────────────────────────────────────

function HookCard({ props, layout, durationInFrames, isStatic = false }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { s, L } = layout;
  const { colors } = props.brand;
  const exit = useExitOpacity(durationInFrames ?? 1);
  const pop = isStatic ? 1 : spring({ frame, fps, config: springs.pop });
  const scale = interpolate(pop, [0, 1], [0.92, 1]);
  const bar = isStatic ? 1 : interpolate(frame, [6, 22], [0, 1], { ...clampOpts, easing: Easing.out(Easing.cubic) });

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        opacity: isStatic ? 1 : Math.min(pop, exit),
        transform: `scale(${scale})`,
      }}
    >
      <div
        style={{
          fontSize: s(L.hookFont),
          fontWeight: 900,
          lineHeight: 1.08,
          letterSpacing: "-2px",
          color: colors.text,
        }}
      >
        {props.hook}
      </div>
      <div
        style={{
          marginTop: s(L.gap * 1.4),
          height: s(14),
          width: `${Math.round(bar * 46)}%`,
          borderRadius: s(7),
          background: colors.accent,
        }}
      />
    </AbsoluteFill>
  );
}

function StatTile({ label, value, color, colors, layout, basis, style }) {
  const { s, L } = layout;
  return (
    <div
      style={{
        flex: `0 0 ${basis}`,
        boxSizing: "border-box",
        padding: `${s(L.tileLabelFont * 0.8)}px ${s(L.tileLabelFont)}px`,
        borderRadius: s(20),
        background: colors.surface,
        ...style,
      }}
    >
      <div style={{ fontSize: s(L.tileLabelFont), fontWeight: 700, color: colors.muted, textTransform: "uppercase", letterSpacing: "1.5px" }}>
        {label}
      </div>
      <div style={{ fontSize: s(L.tileValueFont), fontWeight: 900, color, marginTop: s(6), letterSpacing: "-1px" }}>{value}</div>
    </div>
  );
}

function SetupCard({ props, layout, durationInFrames }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { s, L, key } = layout;
  const { colors } = props.brand;
  const { numbers } = props;
  const exit = useExitOpacity(durationInFrames);
  const net = formatNetDebitCredit(numbers.netDebitCredit);
  const tiles = [
    { label: "Spot", value: formatPrice(props.spot), color: colors.text },
    { label: net.label, value: net.value, color: colors.text },
    { label: "Max loss", value: formatAmountOrUnlimited(numbers.maxLoss, { loss: true }), color: colors.negative },
    { label: "Max gain", value: formatAmountOrUnlimited(numbers.maxGain), color: colors.accent },
    {
      label: numbers.breakevens.length > 1 ? "Breakevens" : "Breakeven",
      value: numbers.breakevens.map(formatPrice).join(" / "),
      color: colors.accent,
    },
  ];
  const tileGap = s(L.gap * 0.5);
  const basis = key === "vertical" ? `calc(50% - ${tileGap / 2}px)` : `calc(33.333% - ${(tileGap * 2) / 3}px)`;
  const enter = (i) => fadeUpEaseOut(frame, 4 + i * 4, fps, 300, s(18));

  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column", justifyContent: "center", opacity: exit }}>
      <div style={{ ...enter(0), fontSize: s(L.tickerFont), fontWeight: 900, lineHeight: 0.95, letterSpacing: "-4px", color: colors.text }}>
        {props.ticker}
      </div>
      <div style={{ ...enter(1), fontSize: s(L.strategyFont), fontWeight: 800, color: colors.accent, marginTop: s(L.gap * 0.6) }}>
        {props.strategyLabel}
      </div>
      <div style={{ ...enter(2), fontSize: s(L.legsFont), fontWeight: 600, lineHeight: 1.3, color: colors.muted, marginTop: s(L.gap * 0.5) }}>
        {props.legsSummary}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: tileGap, marginTop: s(L.gap * 1.2) }}>
        {tiles.map((tile, i) => (
          <StatTile
            key={tile.label}
            {...tile}
            colors={colors}
            layout={layout}
            basis={key === "vertical" && i === tiles.length - 1 ? "100%" : basis}
            style={enter(3 + i)}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
}

/** Marker price keyframes: spot during the chart segment, then each beat's markerPrice. */
function markerPriceAt(frame, keyframes, transitionFrames) {
  let price = keyframes[0].price;
  for (let i = 1; i < keyframes.length; i += 1) {
    const k = keyframes[i];
    if (frame < k.frame) break;
    const t = interpolate(frame, [k.frame, k.frame + transitionFrames], [0, 1], {
      ...clampOpts,
      easing: Easing.inOut(Easing.cubic),
    });
    price = price + (k.price - price) * t;
  }
  return price;
}

function BigNumber({ beat, layout, colors, durationInFrames }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { s, L } = layout;
  const exit = useExitOpacity(durationInFrames);
  const pop = spring({ frame: frame - 4, fps, config: springs.settle });
  const value = formatEmphasis(beat.emphasis);
  const color = beat.emphasis.value < 0 ? colors.negative : colors.accent;
  return (
    <div style={{ opacity: Math.min(pop, exit), transform: `translateY(${interpolate(pop, [0, 1], [s(30), 0])}px)` }}>
      <div style={{ fontSize: s(L.bigNumberFont), fontWeight: 900, lineHeight: 1, letterSpacing: "-5px", color }}>{value}</div>
      {beat.emphasis.label ? (
        <div style={{ fontSize: s(L.bigLabelFont), fontWeight: 700, color: colors.muted, marginTop: s(8) }}>{beat.emphasis.label}</div>
      ) : null}
    </div>
  );
}

function ChartAndBeats({ props, layout, frameInfo, chartSegment, beatSegments }) {
  const frame = useCurrentFrame();
  const { s, L } = layout;
  const { colors } = props.brand;
  const chartDur = chartSegment.durationInFrames;
  const legendFont = s(L.chartFont);
  const legendHeight = Math.round(legendFont * 1.6);
  const hasEmphasis = props.beats.some((b) => b.emphasis);
  const bigBlock = hasEmphasis ? s(L.bigNumberFont) + s(L.bigLabelFont) * 1.4 + s(L.gap) : 0;

  const fullH = Math.min(s(L.chartHeight), frameInfo.stageHeight - legendHeight - s(L.gap));
  const compactH = Math.min(s(L.chartHeightCompact), frameInfo.stageHeight - legendHeight - bigBlock - s(L.gap));
  const compactT = interpolate(frame, [chartDur, chartDur + 14], [0, 1], { ...clampOpts, easing: Easing.inOut(Easing.cubic) });
  const chartH = Math.round(fullH + (compactH - fullH) * compactT);

  const progress = interpolate(frame, [6, Math.round(chartDur * 0.6)], [0, 1], { ...clampOpts, easing: Easing.inOut(Easing.quad) });
  const markersOpacity = interpolate(frame, [Math.round(chartDur * 0.55), Math.round(chartDur * 0.72)], [0, 1], clampOpts);
  const markerOpacity = interpolate(frame, [Math.round(chartDur * 0.7), Math.round(chartDur * 0.85)], [0, 1], clampOpts);
  const enter = interpolate(frame, [0, 10], [0, 1], clampOpts);
  const totalDur = chartDur + beatSegments.reduce((a, b) => a + b.durationInFrames, 0);
  const exit = interpolate(frame, [totalDur - 6, totalDur], [1, 0], clampOpts);

  const keyframes = [{ frame: 0, price: props.spot }];
  let last = props.spot;
  beatSegments.forEach((seg) => {
    const target = props.beats[seg.index].markerPrice ?? last;
    keyframes.push({ frame: seg.from - chartSegment.from, price: target });
    last = target;
  });
  const markerPrice = markerPriceAt(frame, keyframes, 24);

  return (
    <AbsoluteFill style={{ opacity: Math.min(enter, exit) }}>
      <PayoffChart
        idPrefix="clip-payoff"
        width={frameInfo.contentWidth}
        height={chartH}
        expiration={props.payoff.expiration}
        tPlus0={props.payoff.tPlus0}
        breakevens={props.numbers.breakevens}
        maxLoss={props.numbers.maxLoss}
        ticker={props.ticker}
        markerPrice={markerPrice}
        progress={progress}
        markersOpacity={markersOpacity}
        markerOpacity={markerOpacity}
        colors={colors}
        fontSize={s(L.chartFont)}
      />
      <div style={{ height: legendHeight, display: "flex", alignItems: "center" }}>
        <PayoffLegend hasTPlus0={Boolean(props.payoff.tPlus0)} colors={colors} fontSize={legendFont} />
      </div>
      <div style={{ position: "relative", flex: 1, marginTop: s(L.gap * 0.5) }}>
        {beatSegments.map((seg) =>
          props.beats[seg.index].emphasis ? (
            <Sequence
              key={`beat-${seg.index}`}
              from={seg.from - chartSegment.from}
              durationInFrames={seg.durationInFrames}
              layout="none"
            >
              <BigNumber beat={props.beats[seg.index]} layout={layout} colors={colors} durationInFrames={seg.durationInFrames} />
            </Sequence>
          ) : null
        )}
      </div>
    </AbsoluteFill>
  );
}

function CtaCard({ props, layout, durationInFrames }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { s, L } = layout;
  const { colors } = props.brand;
  const exit = useExitOpacity(durationInFrames);
  const pop = spring({ frame: frame - 8, fps, config: springs.rise });
  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", opacity: exit }}>
      <div style={{ ...fadeUpEaseOut(frame, 0, fps, 300, s(20)), fontSize: s(L.ctaFont), fontWeight: 900, lineHeight: 1.05, letterSpacing: "-2px", color: colors.text }}>
        {props.cta.text}
      </div>
      <div
        style={{
          marginTop: s(L.gap * 1.6),
          opacity: pop,
          transform: `scale(${interpolate(pop, [0, 1], [0.9, 1])})`,
          padding: `${s(L.urlFont * 0.4)}px ${s(L.urlFont * 0.8)}px`,
          borderRadius: s(L.urlFont),
          background: colors.primary,
          color: "#FFFFFF",
          fontSize: s(L.urlFont),
          fontWeight: 800,
          letterSpacing: "-0.5px",
        }}
      >
        {props.cta.url}
      </div>
      <div style={{ ...fadeUpEaseOut(frame, 16, fps, 300, s(12)), marginTop: s(L.gap * 1.4), fontSize: s(L.legsFont), fontWeight: 600, color: colors.muted }}>
        {`${props.ticker} · ${props.strategyLabel}`}
      </div>
    </AbsoluteFill>
  );
}

function EndCard({ props, layout }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { s, L } = layout;
  const { colors } = props.brand;
  // Text is fully visible within ~0.3 s, leaving the rest of the >= 3 s card readable.
  const enter = (i) => fadeUpEaseOut(frame, i * 3, fps, 250, s(12));
  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: s(L.gap * 1.1) }}>
      <div style={enter(0)}>
        <BrandMark brand={props.brand} size={s(L.logoSize * 1.4)} fontSize={s(L.brandFont * 1.5)} />
      </div>
      <div style={{ ...enter(1), fontSize: s(L.endTitleFont), fontWeight: 900, lineHeight: 1.2, color: colors.text }}>
        {FIXED_DISCLAIMER_LINE}
      </div>
      <div style={{ ...enter(2), fontSize: s(L.endBodyFont), fontWeight: 500, lineHeight: 1.4, color: colors.text }}>
        {props.disclaimer}
      </div>
      <div style={{ ...enter(3), fontSize: s(L.endMetaFont), fontWeight: 600, lineHeight: 1.4, color: colors.muted }}>
        {modeledDataLine(props.dataAsOf)}
      </div>
    </AbsoluteFill>
  );
}

// ── Compositions ────────────────────────────────────────────

function Shell({ props, layout, frameInfo, children, captions, footerOpacity = 1 }) {
  const { safeTop, safeBottom, sidePad } = layout;
  return (
    <AbsoluteFill style={{ backgroundColor: props.brand.colors.background, fontFamily: VIDEO_FONT_FAMILY }}>
      <div
        style={{
          position: "absolute",
          top: safeTop,
          bottom: safeBottom,
          left: sidePad,
          right: sidePad,
          display: "flex",
          flexDirection: "column",
          gap: frameInfo.gap,
        }}
      >
        <Header props={props} layout={layout} height={frameInfo.headerHeight} />
        <div style={{ position: "relative", height: frameInfo.stageHeight, flexShrink: 0 }}>{children}</div>
        <div style={{ height: frameInfo.captionHeight, flexShrink: 0 }}>{captions}</div>
        <Footer props={props} fontSize={frameInfo.footerFont} height={frameInfo.footerHeight} opacity={footerOpacity} />
      </div>
    </AbsoluteFill>
  );
}

export function ScenarioExplainerClip(rawProps) {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const { props, timeline, words, chunks, layout } = useClipModel(rawProps);
  const frameInfo = computeFrame(layout, props, width, height);
  const seg = Object.fromEntries(timeline.segments.filter((x) => x.key !== "beat").map((x) => [x.key, x]));
  const beatSegments = timeline.segments.filter((x) => x.key === "beat");
  const chartSpan = seg.chart.durationInFrames + beatSegments.reduce((a, b) => a + b.durationInFrames, 0);
  // The end card carries the full disclaimer; the footer covers every other frame.
  const footerOpacity = frame >= seg.end.from ? 0 : 1;

  return (
    <>
      {props.voiceover ? <Audio src={props.voiceover.audioUrl} /> : null}
      <Shell
        props={props}
        layout={layout}
        frameInfo={frameInfo}
        footerOpacity={footerOpacity}
        captions={
          <SocialCaptions
            words={words}
            chunks={chunks}
            colors={props.brand.colors}
            fontSize={layout.s(layout.L.captionFont)}
            height={frameInfo.captionHeight}
          />
        }
      >
        <Sequence from={seg.hook.from} durationInFrames={seg.hook.durationInFrames}>
          <HookCard props={props} layout={layout} durationInFrames={seg.hook.durationInFrames} />
        </Sequence>
        <Sequence from={seg.setup.from} durationInFrames={seg.setup.durationInFrames}>
          <SetupCard props={props} layout={layout} durationInFrames={seg.setup.durationInFrames} />
        </Sequence>
        <Sequence from={seg.chart.from} durationInFrames={chartSpan}>
          <ChartAndBeats
            props={props}
            layout={layout}
            frameInfo={frameInfo}
            chartSegment={seg.chart}
            beatSegments={beatSegments}
          />
        </Sequence>
        <Sequence from={seg.cta.from} durationInFrames={seg.cta.durationInFrames}>
          <CtaCard props={props} layout={layout} durationInFrames={seg.cta.durationInFrames} />
        </Sequence>
        <Sequence from={seg.end.from}>
          <EndCard props={props} layout={layout} />
        </Sequence>
      </Shell>
    </>
  );
}

/** Cover image: hook-card style with the full payoff curve. */
export function ScenarioExplainerThumbnail(rawProps) {
  const { width, height } = useVideoConfig();
  const { props, layout } = useClipModel(rawProps);
  const frameInfo = computeFrame(layout, props, width, height);
  const { s, L } = layout;
  const chartH = Math.min(s(L.chartHeightCompact), Math.round(frameInfo.stageHeight * 0.48));

  return (
    <Shell props={props} layout={layout} frameInfo={frameInfo} captions={null}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: frameInfo.stageHeight - chartH - s(L.gap) }}>
        <HookCard props={props} layout={layout} isStatic />
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0 }}>
        <PayoffChart
          idPrefix="thumb-payoff"
          width={frameInfo.contentWidth}
          height={chartH}
          expiration={props.payoff.expiration}
          tPlus0={props.payoff.tPlus0}
          breakevens={props.numbers.breakevens}
          maxLoss={props.numbers.maxLoss}
          ticker={props.ticker}
          markerPrice={props.spot}
          colors={props.brand.colors}
          fontSize={s(L.chartFont)}
        />
      </div>
    </Shell>
  );
}
